import { Text } from '@fluentui/react-components'
import { PublicClientApplication } from '@azure/msal-browser'
import { AxiosError, AxiosResponse } from 'axios'
import { UserDto } from '../../config/userConfig'
import * as React from 'react'
import { Dispatch, ReactElement } from 'react'
import {
    AuthenticationPhase,
    Host,
    Identity,
    IdentityStateAction,
    PersistentIdentityContextProps,
    SecurityAuthority,
} from '../../context/IdentityContext'
import { isOfficeInitialized } from '../../helpers/outlookHelper'
import useAxios from '../../hooks/useAxios'
import useAcquireBootstrapToken from './useAcquireBootstrapToken'
import { AppAction } from '../../config/appConfig'
import { useTranslation } from 'react-i18next'

type Props = {
    phase: AuthenticationPhase
    setPhase: (phase: AuthenticationPhase) => void
    bootstrapToken: string
    handleBootstrapToken: (token?: string) => void
    setError: (error: ReactElement | string) => void
    state: PersistentIdentityContextProps
    pca: PublicClientApplication
    dispatch: Dispatch<IdentityStateAction>
}

export default function useHandleAuthenticationPhase(): (props: Props) => void {
    const axios = useAxios()
    const acquireBootstrapToken = useAcquireBootstrapToken()
    const { t: translate } = useTranslation()

    return ({ phase, setPhase, bootstrapToken, handleBootstrapToken, setError, state, pca, dispatch }: Props) => {
        switch (phase) {
            // Discover the username automatically.
            case AuthenticationPhase.DiscoverUsername:
                if (state.username) {
                    // If we have a username in our persistent state, this is the one to go.
                    Identity.current.selectAccount(state.username)
                } else if (isOfficeInitialized() && Office.context?.mailbox?.userProfile?.emailAddress) {
                    Identity.current.selectAccount(Office.context.mailbox.userProfile.emailAddress)
                } else if (pca.getAllAccounts()?.length === 1) {
                    // If we have exactly one account in MSAL memory, take this one.
                    // Try to acquire a bootstrap token silently if we already have exactly one account in memory.
                    Identity.current.selectAccount(pca.getAllAccounts()[0].username)
                } else {
                    // Let the user select an account.
                    setPhase(AuthenticationPhase.SelectAccount)
                }
                break

            // Require user interaction to discover the username, there's nothing to be done, because this is the
            // manual starting point.
            case AuthenticationPhase.SelectAccount:
                // Just make sure the memory is all cleared up.
                handleBootstrapToken(undefined)
                break

            // Get a bootstrap token for the selected user. This is always in succession of account selection.
            case AuthenticationPhase.FetchBootstrapToken:
                if (!state.username) {
                    setPhase(AuthenticationPhase.SelectAccount)
                    break
                }
                acquireBootstrapToken(state.username, pca, handleBootstrapToken)
                    .then(handleBootstrapToken)
                    .catch(() => {
                        setError(
                            <Text>{translate('authentication.account.not.found', { username: state?.username })}</Text>,
                        )
                        setPhase(AuthenticationPhase.SelectAccount)
                    })
                break

            case AuthenticationPhase.DiscoverHosts:
                if (!bootstrapToken || !state.username) {
                    setPhase(AuthenticationPhase.SelectAccount)
                    break
                }

                // If we have a bootstrap token, we should try to redeem it.
                axios
                    .get<Host[]>(`/api/host`, {
                        baseURL: process.env.ROUTER_HOST,
                        headers: { Authorization: `Bearer ${bootstrapToken}`, SuppressErrors: 'true' },
                    })
                    .then((response: AxiosResponse<Host[]>) => {
                        dispatch({ type: 'hosts', payload: response.data || [] })
                        if (response.data?.length === 1) {
                            Identity.current.selectHost(response.data[0])
                        } else {
                            setPhase(AuthenticationPhase.SelectHost)
                        }
                    })
                    .catch((error: AxiosError<string>) => {
                        if (error?.response?.status !== 404) {
                            setError(
                                <Text>
                                    {translate('authentication.account.fetch.hosts.error', {
                                        username: state?.username,
                                        error: error?.response?.data || error?.message,
                                    })}
                                </Text>,
                            )
                        }

                        // The make sure that the current username is logged out, don't log out of MSAL.
                        Identity.current.logout(AppAction.LogOut, state.username, false)
                    })
                break

            // Require user interaction to select the host.
            case AuthenticationPhase.SelectHost:
                if (!bootstrapToken || !state.username || !state.hosts) {
                    setPhase(AuthenticationPhase.SelectAccount)
                    break
                }
                break

            case AuthenticationPhase.FetchTokens:
                if (!state.currentHost) {
                    setPhase(AuthenticationPhase.SelectHost)
                    break
                }
                Object.values(SecurityAuthority).map(authority => {
                    Identity.current
                        .acquireToken(authority)
                        .then(() => {
                            if (authority === SecurityAuthority.Hammock) {
                                setPhase(AuthenticationPhase.FetchUser)
                            }
                        })
                        .catch(error => {
                            if (authority === SecurityAuthority.Hammock) {
                                setError(
                                    <Text>
                                        {translate('authentication.token.fetch.error', {
                                            currentHost: state.currentHost?.Name,
                                            error: error?.response?.data?.error_description || error?.message,
                                        })}
                                    </Text>,
                                )
                                setPhase(AuthenticationPhase.SelectHost)
                            }
                        })
                })
                break

            case AuthenticationPhase.FetchUser:
                if (!state.tokens[SecurityAuthority.Hammock]) {
                    setPhase(AuthenticationPhase.FetchTokens)
                    break
                }
                axios
                    .get<UserDto>('/user/search', { params: { email: state.username } })
                    .then(response => dispatch({ type: 'user', payload: response.data }))
                    .catch(() => {
                        setError(
                            <Text>
                                {translate('authentication.token.fetch.error', {
                                    username: state?.username,
                                    currentHost: state.currentHost?.Name,
                                })}
                            </Text>,
                        )
                        setPhase(AuthenticationPhase.SelectHost)
                    })
                break
        }
    }
}
